import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import interfaceApi from '@/api/admin/interfaceApi'

/**
 * Interface Slice - Query-based caching for interface data
 * 
 * Available Thunk Actions:
 * 
 * 1. fetchAllInterfaces - General purpose fetcher (backward compatibility)
 *    dispatch(fetchAllInterfaces({ PageSize: 0 }))
 * 
 * 2. fetchInterfacesByQuery - Explicit query-based fetching
 *    dispatch(fetchInterfacesByQuery({ PageSize: 10, PageIndex: 1, status: true }))
 * 
 * 3. fetchInterfaceById - Fetch single interface and update all caches
 *    dispatch(fetchInterfaceById('123'))
 * 
 * 4. refreshInterfacesAll - Force refresh complete dataset (PageSize=0)
 *    dispatch(refreshInterfacesAll())
 * 
 * Usage Examples:
 * 
 * // Get all interfaces (complete dataset)
 * dispatch(refreshInterfacesAll())
 * const allInterfaces = useAppSelector(selectInterfacesAll)
 * 
 * // Get paginated data with query caching
 * const queryKey = createQueryKey({ PageSize: 10, PageIndex: 1 })
 * dispatch(fetchInterfacesByQuery({ PageSize: 10, PageIndex: 1 }))
 * const pagedData = useAppSelector(state => selectInterfaceQueryData(state, queryKey))
 * const isLoading = useAppSelector(state => selectInterfaceQueryLoading(state, queryKey))
 * 
 * // Get single interface (updates all caches automatically)
 * dispatch(fetchInterfaceById('123'))
 * const interface = useAppSelector(state => selectInterfaceById(state, 123))
 * 
 * // Check global loading state
 * const globalLoading = useAppSelector(selectInterfacesGlobalLoading)
 * 
 * // Clear specific query cache
 * dispatch(clearQueryCache(queryKey))
 * 
 * // Clear all query caches
 * dispatch(clearAllQueries())
 */

export interface WeatherLinkStation {
  weatherLinkStationId: number
  name: string
  location: string
  sourceName: string
}

export interface Organization {
  id: number
  name: string
  role: string | null
  description: string
}

export interface InterfaceItem {
  id: number
  interfaceName: string
  interfaceCustomName: string | null
  organizations: Organization[] | null
  status: boolean
  createdAt: string
  updatedAt: string
  weatherLinkStations: WeatherLinkStation[] | null
  state: string
  pictureUrl: string | null
  pictureFileName: string | null
  timeZoneId: string | null
  timeZoneOriginId: string | null
  timeZone: string | null
  timeZoneName: string | null
  country: string | null
  isObservedDst: boolean
  softWareVersion: number
}

export interface InterfaceApiResponse {
  page: number
  size: number
  totalPage: number
  totalCount: number
  mobileTotalCount: number | null
  data: InterfaceItem[]
  sort: string
}

export interface InterfaceQueryResult {
  data: InterfaceItem[]
  pagination: {
    page: number
    size: number
    totalPage: number
    totalCount: number
    mobileTotalCount: number | null
    sort: string
  }
  isLoading: boolean
  error: string | null
  lastFetched: number
}

export interface InterfaceState {
  queries: Record<string, InterfaceQueryResult>
  interfacesAll: InterfaceItem[]
  isLoadingGlobal: boolean
  errorGlobal: string | null
}

const initialState: InterfaceState = {
  queries: {},
  interfacesAll: [],
  isLoadingGlobal: false,
  errorGlobal: null,
}

const generateQueryKey = (params: Record<string, any>): string => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((result: Record<string, any>, key: string) => {
      result[key] = params[key]
      return result
    }, {})
  
  return JSON.stringify(sortedParams)
}

export const fetchAllInterfaces = createAsyncThunk(
  'interface/fetchAll',
  async (params: { PageSize?: number; PageIndex?: number; [key: string]: any } = {}, { rejectWithValue }) => {
    try {
      const queryKey = generateQueryKey(params)
      const { data } = await interfaceApi.getAll(params)
      return { 
        data: data as InterfaceApiResponse, 
        queryKey,
        params
      }
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to fetch interfaces', queryKey: generateQueryKey(params) })
    }
  },
)

export const fetchInterfacesByQuery = createAsyncThunk(
  'interface/fetchByQuery',
  async (params: { PageSize?: number; PageIndex?: number; [key: string]: any }, { rejectWithValue }) => {
    try {
      const queryKey = generateQueryKey(params)
      const { data } = await interfaceApi.getAll(params)
      return { 
        data: data as InterfaceApiResponse, 
        queryKey,
        params
      }
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to fetch interfaces by query', queryKey: generateQueryKey(params) })
    }
  },
)

export const fetchInterfaceById = createAsyncThunk(
  'interface/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await interfaceApi.getById(id)
      return data as InterfaceItem
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch interface by ID')
    }
  },
)

export const refreshInterfacesAll = createAsyncThunk(
  'interface/refreshAll',
  async (_, { rejectWithValue }) => {
    try {
      const params = { PageSize: 0 }
      const queryKey = generateQueryKey(params)
      const { data } = await interfaceApi.getAll(params)
      return { 
        data: data as InterfaceApiResponse, 
        queryKey,
        params
      }
    } catch (error: any) {
      return rejectWithValue({ error: error.message || 'Failed to refresh all interfaces', queryKey: generateQueryKey({ PageSize: 0 }) })
    }
  },
)

const interfaceSlice = createSlice({
  name: 'interface',
  initialState,
  reducers: {
    clearInterfacesAll: (state) => {
      state.interfacesAll = []
      state.errorGlobal = null
    },
    clearQueryCache: (state, action: PayloadAction<string>) => {
      delete state.queries[action.payload]
    },
    clearAllQueries: (state) => {
      state.queries = {}
    },
    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.errorGlobal = action.payload
    },
    setInterfacesAll: (state, action: PayloadAction<InterfaceItem[]>) => {
      state.interfacesAll = action.payload
      state.errorGlobal = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllInterfaces.pending, (state, action) => {
        const queryKey = generateQueryKey(action.meta.arg)
        state.isLoadingGlobal = true
        if (!state.queries[queryKey]) {
          state.queries[queryKey] = {
            data: [],
            pagination: { page: 1, size: 0, totalPage: 1, totalCount: 0, mobileTotalCount: null, sort: 'asc' },
            isLoading: true,
            error: null,
            lastFetched: Date.now()
          }
        } else {
          state.queries[queryKey].isLoading = true
          state.queries[queryKey].error = null
        }
      })
      .addCase(fetchAllInterfaces.fulfilled, (state, action) => {
        const { data, queryKey, params } = action.payload
        state.isLoadingGlobal = false
        
        state.queries[queryKey] = {
          data: data.data,
          pagination: {
            page: data.page,
            size: data.size,
            totalPage: data.totalPage,
            totalCount: data.totalCount,
            mobileTotalCount: data.mobileTotalCount,
            sort: data.sort,
          },
          isLoading: false,
          error: null,
          lastFetched: Date.now()
        }
        
        if (params.PageSize === 0 || !params.PageSize || params.PageSize >= data.totalCount) {
          state.interfacesAll = data.data
        }
      })
      .addCase(fetchAllInterfaces.rejected, (state, action) => {
        state.isLoadingGlobal = false
        const payload = action.payload as { error: string; queryKey: string }
        state.errorGlobal = payload.error
        
        if (state.queries[payload.queryKey]) {
          state.queries[payload.queryKey].isLoading = false
          state.queries[payload.queryKey].error = payload.error
        }
      })
      .addCase(fetchInterfacesByQuery.pending, (state, action) => {
        const queryKey = generateQueryKey(action.meta.arg)
        state.isLoadingGlobal = true
        if (!state.queries[queryKey]) {
          state.queries[queryKey] = {
            data: [],
            pagination: { page: 1, size: 0, totalPage: 1, totalCount: 0, mobileTotalCount: null, sort: 'asc' },
            isLoading: true,
            error: null,
            lastFetched: Date.now()
          }
        } else {
          state.queries[queryKey].isLoading = true
          state.queries[queryKey].error = null
        }
      })
      .addCase(fetchInterfacesByQuery.fulfilled, (state, action) => {
        const { data, queryKey, params } = action.payload
        state.isLoadingGlobal = false
        
        state.queries[queryKey] = {
          data: data.data,
          pagination: {
            page: data.page,
            size: data.size,
            totalPage: data.totalPage,
            totalCount: data.totalCount,
            mobileTotalCount: data.mobileTotalCount,
            sort: data.sort,
          },
          isLoading: false,
          error: null,
          lastFetched: Date.now()
        }
        
        if (params.PageSize === 0 || !params.PageSize || params.PageSize >= data.totalCount) {
          state.interfacesAll = data.data
        }
      })
      .addCase(fetchInterfacesByQuery.rejected, (state, action) => {
        state.isLoadingGlobal = false
        const payload = action.payload as { error: string; queryKey: string }
        state.errorGlobal = payload.error
        
        if (state.queries[payload.queryKey]) {
          state.queries[payload.queryKey].isLoading = false
          state.queries[payload.queryKey].error = payload.error
        }
      })
      .addCase(fetchInterfaceById.pending, (state) => {
        state.isLoadingGlobal = true
      })
      .addCase(fetchInterfaceById.fulfilled, (state, action) => {
        state.isLoadingGlobal = false
        const interfaceItem = action.payload
        
        const existingIndex = state.interfacesAll.findIndex(item => item.id === interfaceItem.id)
        if (existingIndex >= 0) {
          state.interfacesAll[existingIndex] = interfaceItem
        } else {
          state.interfacesAll.push(interfaceItem)
        }
        
        Object.keys(state.queries).forEach(queryKey => {
          const query = state.queries[queryKey]
          const itemIndex = query.data.findIndex(item => item.id === interfaceItem.id)
          if (itemIndex >= 0) {
            query.data[itemIndex] = interfaceItem
          }
        })
      })
      .addCase(fetchInterfaceById.rejected, (state, action) => {
        state.isLoadingGlobal = false
        state.errorGlobal = action.payload as string
      })
      .addCase(refreshInterfacesAll.pending, (state, action) => {
        const queryKey = generateQueryKey({ PageSize: 0 })
        state.isLoadingGlobal = true
        if (!state.queries[queryKey]) {
          state.queries[queryKey] = {
            data: [],
            pagination: { page: 1, size: 0, totalPage: 1, totalCount: 0, mobileTotalCount: null, sort: 'asc' },
            isLoading: true,
            error: null,
            lastFetched: Date.now()
          }
        } else {
          state.queries[queryKey].isLoading = true
          state.queries[queryKey].error = null
        }
      })
      .addCase(refreshInterfacesAll.fulfilled, (state, action) => {
        const { data, queryKey } = action.payload
        state.isLoadingGlobal = false
        
        state.queries[queryKey] = {
          data: data.data,
          pagination: {
            page: data.page,
            size: data.size,
            totalPage: data.totalPage,
            totalCount: data.totalCount,
            mobileTotalCount: data.mobileTotalCount,
            sort: data.sort,
          },
          isLoading: false,
          error: null,
          lastFetched: Date.now()
        }
        
        state.interfacesAll = data.data
      })
      .addCase(refreshInterfacesAll.rejected, (state, action) => {
        state.isLoadingGlobal = false
        const payload = action.payload as { error: string; queryKey: string }
        state.errorGlobal = payload.error
        
        if (state.queries[payload.queryKey]) {
          state.queries[payload.queryKey].isLoading = false
          state.queries[payload.queryKey].error = payload.error
        }
      })
  },
})

export const { clearInterfacesAll, clearQueryCache, clearAllQueries, setGlobalError, setInterfacesAll } = interfaceSlice.actions

export const selectInterfacesAll = (state: { interface: InterfaceState }) => state.interface.interfacesAll
export const selectInterfacesGlobalLoading = (state: { interface: InterfaceState }) => state.interface.isLoadingGlobal
export const selectInterfacesGlobalError = (state: { interface: InterfaceState }) => state.interface.errorGlobal

export const selectInterfaceQuery = (state: { interface: InterfaceState }, queryKey: string) => 
  state.interface.queries[queryKey]

export const selectInterfaceQueryData = (state: { interface: InterfaceState }, queryKey: string) =>
  state.interface.queries[queryKey]?.data || []

export const selectInterfaceQueryPagination = (state: { interface: InterfaceState }, queryKey: string) =>
  state.interface.queries[queryKey]?.pagination

export const selectInterfaceQueryLoading = (state: { interface: InterfaceState }, queryKey: string) =>
  state.interface.queries[queryKey]?.isLoading || false

export const selectInterfaceQueryError = (state: { interface: InterfaceState }, queryKey: string) =>
  state.interface.queries[queryKey]?.error

export const selectInterfaceQueryLastFetched = (state: { interface: InterfaceState }, queryKey: string) =>
  state.interface.queries[queryKey]?.lastFetched

export const selectInterfaceById = (state: { interface: InterfaceState }, id: number) =>
  state.interface.interfacesAll.find(item => item.id === id)

export const selectActiveInterfaces = (state: { interface: InterfaceState }) =>
  state.interface.interfacesAll.filter(item => item.status === true)

export const selectOnlineInterfaces = (state: { interface: InterfaceState }) =>
  state.interface.interfacesAll.filter(item => item.state === 'Online')

export const selectInterfacesByOrganization = (state: { interface: InterfaceState }, organizationId: number) =>
  state.interface.interfacesAll.filter(item => 
    item.organizations?.some(org => org.id === organizationId)
  )

export const createQueryKey = (params: Record<string, any>): string => generateQueryKey(params)

export default interfaceSlice.reducer